import { configureStore } from "@reduxjs/toolkit";
import parentReducer from "@/components/state/parent/parentSlice";
import childrenReducer from "@/components/state/parent/childrenSlice";
import logoutReducer from "@/components/state/login/logoutSlice";
import loginReducer from "@/components/state/parent/loginSlice";
import appointmentDashboardReducer from "@/components/state/appointment/appointmentDashboardSlice";
import navigateToReducer from "@/components/state/navigation/navigateToSlice";
import subjectSearchReducer from "@/components/state/subjectSearch/subjectSearchSlice";
import tableSearchReducer from "@/components/state/tableSearch/tableSearchSlice";
import appointmentDashboardStatusFilterReducer from "@/components/state/appointmentDashboard/statusFilterSlice";
import appointmentDashboardCategoryFilterReducer from "@/components/state/appointmentDashboard/categoryFilterSlice";
import appointmentDashboardExecutedByFilterReducer from "@/components/state/appointmentDashboard/executedByFilterSlice";
import appointmentDashboardSubjectFilterReducer from "@/components/state/appointmentDashboard/subjectFilterSlice";
import appointmentDashboardCombinedFilterReducer from "@/components/state/appointmentDashboard/combinedFilterSlice";
import tableConfigReducer from "@/components/state/tableSearch/tableConfigSlice";
import appVersionReducer from "@/components/state/version/versionSlice";
import appointmentDashboardSelectedCalendarDateReducer from "@/components/state/appointmentDashboard/selectedCalendarDaySlice";
import jsonEditorReducer from "@/components/state/jsonEditor/jsonEditorSlice";
import fileListReducer from "@/components/state/fileList/fileListSlice";
import todoReducer from "@/components/state/todo/todoSlice";
import youtubeConsentReducer from "@/components/state/youtube/youtubeConsentSlice";
import loadingReducer from "@/components/state/general/loadingSlice";
import backgroundTasksReducer from "@/components/state/backgroundTasks/backgroundTasksSlice";
import pageHeadingReducer from "@/components/state/pageHeading/pageHeadingSlice";
import managerLmsAccessReducer from "@/components/state/lms/managerLmsAccessSlice";
import appointmentFormPreferencesReducer, { normalizeAppointmentFormPreferences, type AppointmentFormPreferences } from "@/components/state/appointmentDashboard/appointmentFormPreferencesSlice";

import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import type { PersistConfig } from "redux-persist";

const persistAppointmentFormPreferencesConfig: PersistConfig<AppointmentFormPreferences> = {
  key: "appointmentFormPreferences",
  version: 1,
  storage,
  whitelist: ["actionsExpanded", "detailsExpanded"],
  stateReconciler: (inboundState, _originalState, reducedState) => ({
    ...reducedState,
    ...normalizeAppointmentFormPreferences(inboundState),
  }),
};

const persistParentConfig = {
  key: "setParent",
  version: 1,
  storage,
};
const persistLoginConfig = {
  key: "setLogin",
  version: 1,
  storage,
};
const persistSubjectSearchConfig = {
  key: "setSubjectSearch",
  version: 1,
  storage,
};
const persistTableSearchConfig = {
  key: "setTableSearch",
  version: 1,
  storage,
};
const persistAppointmentDashboardStatusFilterConfig = {
  key: "setAppointmentDashboardStatusFilter",
  version: 1,
  storage,
};
const persistAppointmentDashboardCategoryFilterConfig = {
  key: "setAppointmentDashboardcategoryFilter",
  version: 1,
  storage,
};
const persistAppointmentDashboardExecutedByFilterConfig = {
  key: "setAppointmentDashboardExecutedByFilter",
  version: 1,
  storage,
};
const persistAppointmentDashboardSubjectFilterConfig = {
  key: "setAppointmentDashboardSubjectFilter",
  version: 1,
  storage,
};
const persistAppointmentDashboardCombinedFilterConfig = {
  key: "setAppointmentDashboardCombinedFilter",
  version: 1,
  storage,
};
const persistAppointmentDashboardSelectedCalendarDateConfig = {
  key: "setAppointmentDashboardSelectedCalendarDate",
  version: 1,
  storage,
};
const persistTableConfigConfig = {
  key: "setTableConfig",
  version: 1,
  storage,
};
const persistAppVersionConfig = {
  key: "setAppVersion",
  version: 1,
  storage,
};
const persistJsonEditorConfig = {
  key: "setJsonEditor",
  version: 1,
  storage,
};
const persistFileListConfig = {
  key: "setFileList",
  version: 1,
  storage,
};
const persistTodoConfig = {
  key: "setTodo",
  version: 1,
  storage,
};
const persistYouTubeConsentConfig = {
  key: "setYouTubeConsent",
  version: 1,
  storage,
};
const persistBackgroundTaskConfig = {
  key: "setBackgroundTask",
  version: 1,
  storage,
};

const persistedParentReducer = persistReducer(
  persistParentConfig,
  parentReducer,
);
const persistedLoginReducer = persistReducer(persistLoginConfig, loginReducer);
const persistedSubjectSearchReducer = persistReducer(
  persistSubjectSearchConfig,
  subjectSearchReducer,
);
const persistedAppointmentDashboardStatusFilterReducer = persistReducer(
  persistAppointmentDashboardStatusFilterConfig,
  appointmentDashboardStatusFilterReducer,
);
const persistedAppointmentDashboardCategoryFilterReducer = persistReducer(
  persistAppointmentDashboardCategoryFilterConfig,
  appointmentDashboardCategoryFilterReducer,
);
const persistedAppointmentDashboardExecutedByFilterReducer = persistReducer(
  persistAppointmentDashboardExecutedByFilterConfig,
  appointmentDashboardExecutedByFilterReducer,
);
const persistedAppointmentDashboardSubjectFilterReducer = persistReducer(
  persistAppointmentDashboardSubjectFilterConfig,
  appointmentDashboardSubjectFilterReducer,
);
const persistedAppointmentDashboardCombinedFilterReducer = persistReducer(
  persistAppointmentDashboardCombinedFilterConfig,
  appointmentDashboardCombinedFilterReducer,
);
const persistedTableSearchReducer = persistReducer(
  persistTableSearchConfig,
  tableSearchReducer,
);
const persistedAppointmentDashboardCalendarDateReducer = persistReducer(
  persistAppointmentDashboardSelectedCalendarDateConfig,
  appointmentDashboardSelectedCalendarDateReducer,
);
const persistedTableConfigReducer = persistReducer(
  persistTableConfigConfig,
  tableConfigReducer,
);
const persistedAppVersionReducer = persistReducer(
  persistAppVersionConfig,
  appVersionReducer,
);
const persistedJsonEditorReducer = persistReducer(
  persistJsonEditorConfig,
  jsonEditorReducer,
);
const persistedFileListReducer = persistReducer(
  persistFileListConfig,
  fileListReducer,
);
const persistedTodoReducer = persistReducer(persistTodoConfig, todoReducer);
const persistedYouTubeConsentReducer = persistReducer(
  persistYouTubeConsentConfig,
  youtubeConsentReducer,
);
const persistedBackgroundTaskReducer = persistReducer(
  persistBackgroundTaskConfig,
  backgroundTasksReducer,
);
const persistedAppointmentFormPreferencesReducer = persistReducer(
  persistAppointmentFormPreferencesConfig,
  appointmentFormPreferencesReducer,
);
export const store = configureStore({
  reducer: {
    parent: persistedParentReducer,
    children: childrenReducer,
    loading: loadingReducer,
    login: persistedLoginReducer,
    logout: logoutReducer,
    navigateTo: navigateToReducer,
    subjectSearch: persistedSubjectSearchReducer,
    appointmentDashboard: appointmentDashboardReducer,
    tableSearch: persistedTableSearchReducer,
    appointmentDashboardStatusFilter:
      persistedAppointmentDashboardStatusFilterReducer,
    appointmentDashboardCategoryFilter:
      persistedAppointmentDashboardCategoryFilterReducer,
    appointmentDashboardExecutedByFilter:
      persistedAppointmentDashboardExecutedByFilterReducer,
    appointmentDashboardSubjectFilter:
      persistedAppointmentDashboardSubjectFilterReducer,
    appointmentDashboardSelectedCalendarDate:
      persistedAppointmentDashboardCalendarDateReducer,
    appointmentDashboardCombinedFilter:
      persistedAppointmentDashboardCombinedFilterReducer,
    appointmentFormPreferences: persistedAppointmentFormPreferencesReducer,
    tableConfig: persistedTableConfigReducer,
    appVersion: persistedAppVersionReducer,
    jsonEditor: persistedJsonEditorReducer,
    fileList: persistedFileListReducer,
    todo: persistedTodoReducer,
    youtubeConsent: persistedYouTubeConsentReducer,
    backgroundTasks: persistedBackgroundTaskReducer,
    pageHeading: pageHeadingReducer,
    managerLmsAccess: managerLmsAccessReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});
//eslint-disable-next-line
export let persistor = persistStore(store);
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
